import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { produce } from 'immer';
import { compareArrays, isConnectedGuideline } from 'shared/util-common';

import { CommentService } from '@treetop/shared/comments';
import {
  ChapterWidget,
  ComponentListWidget,
  ComponentWidget,
  createComponentListWidget,
  createFlowchartWidget,
  createWorkflowWidget,
  flattenFlowchartElements,
  EditableGuidelineMeta,
  FlowchartWidget,
  Guideline,
  GuidelineBase,
  GuidelineHeader,
  GuidelineMeta,
  GuidelineTag,
  GuidelineTagStatus,
  GuidelineValidationResult,
  GuidelineWidget,
  ResourceTag,
  TextWidget,
  WidgetType,
  WorkflowWidget,
} from '@treetop/shared/model';

import { EditableGuidelineWidget, GuidelineGraphService } from './guideline-graph.service';
import { GuidelineValidatorService } from './guideline-validator.service';
import { GuidelineServiceUtil, sanitizeWidget } from './util/guideline.service.util';
import {
  compareGuidelineHeaders,
  compareGuidelineMeta,
  compareLocalWidgetValues,
  compareWidgets,
  getGuidelineElementTag,
  mergeLocalChanges,
} from './util/update-tag.util';
import { createComponentWidget } from '../../../../../model/src/lib/widgets/component-list.model';
import { createTextWidget } from '../../../../../model/src/lib/widgets/text.model';

type WidgetOverridesMap = {
  workflow: Omit<WorkflowWidget, 'widgetType'>;
  text: Omit<TextWidget, 'widgetType'>;
  component: Omit<ComponentWidget, 'widgetType'>;
  chapter: Omit<ChapterWidget, 'widgetType'>;
  flowchart: Omit<FlowchartWidget, 'widgetType'>;
  'component-list': Omit<ComponentListWidget, 'widgetType'>;
};

@Injectable()
export class GuidelineService {
  private guidelineValidator = inject(GuidelineValidatorService);
  private guidelineGraphService = inject(GuidelineGraphService);
  private commentsService = inject(CommentService, { optional: true });

  private _meta = signal<GuidelineMeta | undefined>(undefined);
  private _header = signal<GuidelineHeader | undefined>(undefined);
  private _headerLocalChanges = signal<Partial<GuidelineHeader> | undefined>(undefined);

  private originalGuideline = signal<Guideline | undefined>(undefined);
  private publishedGuideline = signal<Guideline | undefined>(undefined);

  private updatedWidgetIds = signal<string[]>([]);
  private isNewGuideline = computed(() => !!this.originalGuideline() && !this.originalGuideline().uniqueId);
  public hasUnsavedChanges = computed(
    () => this.isNewGuideline() || this.updatedWidgetIds().length > 0 || this.commentsService.hasUnsavedChanges(),
  );

  /**
   * Returns the current guideline as a Guideline object.
   * Calling this function will reconstruct the guideline from the graph, so use it sparingly.
   */
  public get guideline(): Guideline | undefined {
    const map = this.map();
    const unwrapped = map ? Object.fromEntries(Object.entries(map).map(([id, signal]) => [id, signal()])) : undefined;

    const sanitizedWidgets: { [key: string]: GuidelineWidget } = {};
    if (unwrapped) {
      for (const [id, widget] of Object.entries(unwrapped)) {
        sanitizedWidgets[id] = sanitizeWidget(widget);
      }
    }

    return {
      ...this._meta(),
      ...this._header(),
      ...(this._headerLocalChanges() ?? {}),
      widgetMap: sanitizedWidgets,
      chaptersWidgetIds: this.graph()?.root.children?.map((c) => c.uniqueId) ?? [],
    };
  }

  /**
   * Returns a snapshot of the current guideline elements.
   */
  public get widgets(): GuidelineWidget[] {
    return Object.values(this.map()).map((widgetSignal) => widgetSignal());
  }

  /**
   * Returns a plain record of all widgets with signals unwrapped.
   */
  public get widgetRecord(): Record<string, GuidelineWidget> {
    const raw = this.map();
    return Object.fromEntries(Object.entries(raw).map(([id, s]) => [id, s()]));
  }

  /**
   * Returns a GuidelineElement by its unique ID.
   * */
  public getWidget<T extends GuidelineWidget>(id: string): T {
    return (this.map()[id]?.() as T) ?? undefined;
  }

  /**
   * Returns the guideline data, which includes meta and header information.
   * For connected guidelines, header localChanges (e.g. resourceTags) are merged on top.
   */
  public guidelineData = computed<GuidelineBase | undefined>(() => {
    const meta = this._meta();
    const header = this._header();
    const headerLocalChanges = this._headerLocalChanges();

    if (!meta && !header) {
      return undefined;
    }

    return {
      ...meta,
      ...header,
      ...(headerLocalChanges ?? {}),
    };
  });

  /**
   * Returns graph elements as chapters.
   */
  public chapters = computed(() => this.guidelineGraphService.graph()?.root.children ?? []);

  public chapterIds = computed(() => this.chapters()?.map((c) => c.uniqueId) ?? []);

  public allResourceTags = computed<ResourceTag[]>(() => {
    const widgetTags = Object.values(this.map()).flatMap((s) => {
      const widget = s();
      const tags: ResourceTag[] = [...(widget?.resourceTags ?? []), ...(widget?.localChanges?.resourceTags ?? [])];

      if (widget?.widgetType === 'flowchart') {
        const flowchart = widget as FlowchartWidget;
        flattenFlowchartElements(flowchart).forEach((el) => tags.push(...(el.resourceTags ?? [])));
        const localElements = flowchart.localChanges?.elements;
        if (localElements) {
          flattenFlowchartElements({ ...flowchart, elements: localElements }).forEach((el) => tags.push(...(el.resourceTags ?? [])));
        }
      }

      return tags;
    });
    const guidelineTags = this._headerLocalChanges()?.resourceTags ?? this._header()?.resourceTags ?? [];
    const seen = new Set<string>();

    return [...widgetTags, ...guidelineTags].filter((tag) => {
      if (seen.has(tag.id)) return false;
      seen.add(tag.id);
      return true;
    });
  });

  /**
   * Selects a component or chapter by its unique ID and returns it as a computed signal.
   * @param id - The unique ID of the component or chapter to select.
   */
  public selectElement = <T extends GuidelineWidget = GuidelineWidget>(id: string): Signal<T | undefined> => {
    const elementSignal = this.guidelineGraphService.map()[id];

    if (!elementSignal) {
      return signal(undefined);
    }

    return computed(() => {
      return elementSignal() as T;
    });
  };

  public getPublishedElement<T extends GuidelineWidget = GuidelineWidget>(uniqueId: string): T | undefined {
    return this.publishedGuideline()?.widgetMap?.[uniqueId] as T | undefined;
  }

  public graph = this.guidelineGraphService.graph;
  public map = this.guidelineGraphService.map;
  public numberedChapters = this.guidelineGraphService.numberedChapters;
  public orderedReferences = this.guidelineGraphService.orderedReferences;

  public setGuideline(guideline: Guideline, publishedGuideline?: Guideline): void {
    this.publishedGuideline.set(publishedGuideline ?? this.publishedGuideline());
    this.originalGuideline.set(guideline);
    this.updatedWidgetIds.set([]);
    this._headerLocalChanges.set(undefined);

    this.guidelineGraphService.createGuidelineGraph(guideline);

    const {
      title,
      code,
      ownerId,
      authors,
      allEditorsCanEdit,
      additionalInfo,
      revisionDate,
      revisionDateTimeSpan,
      languageCode,
      codings,
      resourceTags,
      ...meta
    } = guideline;

    this._header.set({
      title,
      code,
      ownerId,
      authors,
      allEditorsCanEdit,
      additionalInfo,
      revisionDate,
      revisionDateTimeSpan,
      languageCode,
      codings,
      resourceTags,
    });

    this._meta.set(meta);
  }

  public addChapter(data?: Partial<ChapterWidget>, parentId?: string, index?: number): void {
    const tag = this.publishedGuideline()
      ? {
          isActive: true,
          status: GuidelineTagStatus.NEW,
        }
      : undefined;

    this.guidelineGraphService.addChapter(
      {
        ...data,
        ...(tag ? { tag } : {}),
      },
      parentId,
      index,
    );

    this._checkParentIfChanged(parentId);
  }

  public updateWidgetTag<T extends GuidelineWidget>(widgetId: string, tag: GuidelineTag | undefined): void {
    const widget = this.getWidget<T>(widgetId);
    if (!widget) {
      return;
    }

    const updatedWidget = produce(widget, (draft) => {
      if (isConnectedGuideline(this.guidelineData().guidelineOrigin)) {
        draft.localChanges.tag = tag;
        return;
      }

      draft.tag = tag;
    });

    this.guidelineGraphService.updateElement(widgetId, updatedWidget);
  }

  /**
   * Updates a chapter or component and synchronizes its tagging state.
   * @param elementId - The unique ID of the element to update.
   * @param updatedWidget - The data to update the element with.
   */
  public updateWidget<T extends GuidelineWidget>(elementId: string, updatedWidget: T): void {
    const publishedWidget = this.publishedGuideline()?.widgetMap?.[elementId];

    const widgetWithUpdatedTag = produce(updatedWidget, (draft) => {
      draft.tag = this.publishedGuideline() ? getGuidelineElementTag(updatedWidget, publishedWidget) : undefined;
    });

    const originalWidget = this.originalGuideline()?.widgetMap?.[elementId];
    if (originalWidget) {
      this.updateChangeTracking(originalWidget.uniqueId, compareWidgets(originalWidget, updatedWidget));
    }

    this.guidelineGraphService.updateElement(elementId, widgetWithUpdatedTag);

    if (widgetWithUpdatedTag.widgetType === 'chapter' || widgetWithUpdatedTag.widgetType === 'component') {
      return;
    }

    // If the updated widget is not a chapter or a component, we need to update the parent chapter or component updatedWidgetIds
    const parentChapter = this.getWidget<ChapterWidget>(updatedWidget.parentId);
    if (!parentChapter) {
      return;
    }

    const updatedParentChapter = produce(parentChapter, (draft) => {
      const updatedIds = new Set(draft.updatedWidgetIds ?? []);
      const hasTag = Boolean(widgetWithUpdatedTag.tag);

      if (hasTag) {
        updatedIds.add(updatedWidget.uniqueId);

        draft.tag ??= {
          isActive: false,
          status: GuidelineTagStatus.CHILD_UPDATED,
        };
      } else {
        updatedIds.delete(updatedWidget.uniqueId);

        if (updatedIds.size === 0 && draft.tag?.status === GuidelineTagStatus.CHILD_UPDATED) {
          draft.tag = undefined;
        }
      }

      draft.updatedWidgetIds = Array.from(updatedIds);
    });

    this.guidelineGraphService.updateElement(updatedParentChapter.uniqueId, updatedParentChapter);
  }

  private _checkParentIfChanged(parentId: string | undefined): void {
    if (!parentId) {
      const originalChapterIds = this.originalGuideline()?.chaptersWidgetIds ?? [];
      const currentChapterIds = this.chapterIds();
      const hasChanged =
        originalChapterIds.length !== currentChapterIds.length || originalChapterIds.some((id, i) => currentChapterIds[i] !== id);

      this.updateChangeTracking('chaptersWidgetIds', hasChanged);

      return;
    }

    const originalParent = this.originalGuideline()?.widgetMap?.[parentId];
    if (!originalParent) {
      return;
    }

    const currentParent = this.getWidget(parentId);
    if (!currentParent) {
      return;
    }

    this.updateChangeTracking(originalParent.uniqueId, compareWidgets(originalParent, currentParent));
  }

  public addWidget<W extends WidgetType>(
    widgetType: W,
    overrides?: Partial<WidgetOverridesMap[W]>,
    parentId?: string,
    index?: number,
  ): string[] {
    const widgets: GuidelineWidget[] = [];

    switch (widgetType) {
      case 'text':
        widgets.push(createTextWidget({ ...overrides, parentId }));
        break;
      case 'component-list':
        widgets.push(createComponentListWidget({ ...overrides, parentId }));
        break;
      case 'workflow':
        widgets.push(createWorkflowWidget({ ...overrides, parentId }));
        break;
      case 'flowchart':
        widgets.push(createFlowchartWidget({ ...overrides, parentId }));
        break;
      case 'component': {
        const componentWidget = createComponentWidget({ ...overrides, parentId });

        const componentDescriptionWidget = createTextWidget({
          parentId: componentWidget.uniqueId,
        });

        componentWidget.widgetIds.push(componentDescriptionWidget.uniqueId);

        widgets.push(componentWidget);
        widgets.push(componentDescriptionWidget);
        break;
      }
      default:
        throw new Error(`Unsupported widget type: ${widgetType}`);
    }

    widgets.forEach((widget) => {
      let tag: GuidelineTag | undefined = undefined;

      if (this.publishedGuideline() && !(widget.widgetType === 'text' && widget.text !== '')) {
        tag = {
          isActive: true,
          status: GuidelineTagStatus.NEW,
        };
      }

      widget.tag = tag;

      this.guidelineGraphService.addWidget(widget, parentId, index);
    });

    if (parentId && widgets.length > 0) {
      this._syncParentOnAdd(widgets[0].uniqueId, parentId);
    }
    this._checkParentIfChanged(parentId);

    return widgets.map((widget) => widget.uniqueId);
  }

  public updateWidgetLocalTag<T extends EditableGuidelineWidget>(elementId: string, tag: GuidelineTag | undefined): void {
    const widget = this.getWidget<T>(elementId);
    if (!widget) {
      return;
    }

    const updatedLocalChanges = produce(widget, (draft) => {
      draft.localChanges = {
        ...draft.localChanges,
        tag,
      };
    });

    this.guidelineGraphService.updateElement(elementId, updatedLocalChanges);
  }

  public updateWidgetLocal<T extends EditableGuidelineWidget>(
    elementId: string,
    updatedLocalChanges: T['localChanges'],
    recalculateTags = true,
  ): void {
    const widget = this.getWidget<T>(elementId);
    const publishedWidget = this.publishedGuideline()?.widgetMap?.[elementId] as T;
    const originalWidget = this.originalGuideline()?.widgetMap?.[elementId] as T;
    const parentElement = this.getWidget<ChapterWidget>(widget.parentId);

    const mergedPublishedWidget = mergeLocalChanges(publishedWidget as TextWidget);
    const mergedOriginalWidget = mergeLocalChanges(originalWidget as TextWidget);
    const mergedCurrentWidget = mergeLocalChanges({ ...widget, localChanges: updatedLocalChanges } as TextWidget);

    const sameAsPublished = compareLocalWidgetValues(mergedCurrentWidget, mergedPublishedWidget);
    const sameAsCurrent = compareLocalWidgetValues(mergedCurrentWidget, widget);
    const sameAsOriginal = compareLocalWidgetValues(mergedCurrentWidget, mergedOriginalWidget);

    this.updateChangeTracking(widget.uniqueId, !sameAsOriginal);

    const updatedElement = produce(widget, (draft) => {
      let tag: GuidelineTag | undefined = updatedLocalChanges?.tag;

      if (recalculateTags) {
        if (parentElement?.tag?.status !== GuidelineTagStatus.NEW) {
          tag =
            sameAsPublished || !mergedPublishedWidget
              ? undefined
              : {
                  ...updatedLocalChanges.tag,
                  status: GuidelineTagStatus.UPDATED,
                };
        }

        const newLocalChanges = {
          ...(sameAsCurrent ? undefined : updatedLocalChanges),
          tag,
        };

        if (Object.values(newLocalChanges).every((value) => value === undefined)) {
          draft.localChanges = undefined;
        } else {
          draft.localChanges = newLocalChanges;
        }
      } else {
        draft.localChanges = updatedLocalChanges;
      }
    });

    this.guidelineGraphService.updateElement(elementId, updatedElement);

    // If the updated widget is not a chapter or a component-list, we need to update the parent chapter's localChanges.updatedWidgetIds
    if (widget.widgetType === 'chapter' || widget.widgetType === 'component-list') {
      return;
    }

    if (!parentElement) return;

    // If the parent chapter is new, we don't need to update its updatedWidgetIds
    if (parentElement?.tag?.status === GuidelineTagStatus.NEW) return;

    const updatedParentChapter = produce(parentElement, (draft) => {
      const updatedLocalChanges = { ...draft.localChanges };

      const updatedIds = new Set(draft.localChanges?.updatedWidgetIds ?? []);

      if (!sameAsPublished) {
        updatedIds.add(updatedElement.uniqueId);

        updatedLocalChanges.tag ??= {
          isActive: false,
          status: GuidelineTagStatus.CHILD_UPDATED,
        };
      } else {
        updatedIds.delete(updatedElement.uniqueId);

        if (updatedIds.size === 0 && draft.localChanges?.tag?.status === GuidelineTagStatus.CHILD_UPDATED) {
          updatedLocalChanges.tag = undefined;
        }
      }

      updatedLocalChanges.updatedWidgetIds = Array.from(updatedIds);

      draft.localChanges = updatedLocalChanges;
    });

    this.guidelineGraphService.updateElement(updatedParentChapter.uniqueId, updatedParentChapter);
  }

  /**
   * Moves a chapter in the graph.
   * @param chapterId - The unique ID of the chapter to move.
   * @param newParentChapterId - The unique ID of the new parent chapter.
   * @param index - The index at which to insert the chapter in the new parent.
   */
  public moveChapter(chapterId: string, newParentChapterId: string, index?: number): void {
    this.guidelineGraphService.moveChapter(chapterId, newParentChapterId, index);

    this._checkParentIfChanged(newParentChapterId);
  }

  public moveWidget(widgetId: string, newParentId: string, index?: number): void {
    this.guidelineGraphService.moveWidget(widgetId, newParentId, index);

    this._checkParentIfChanged(newParentId);
  }

  /**
   * Removes a widget and updates the parent's updatedWidgetIds and tag accordingly.
   * - If the removed widget existed in the published guideline, its ID is added to the parent's updatedWidgetIds (CHILD_UPDATED).
   * - If the removed widget did NOT exist in the published guideline, its ID is removed from the parent's updatedWidgetIds.
   * @param uniqueId - The unique ID of the element to remove.
   */
  public removeWidget(uniqueId: string): void {
    this._syncParentOnRemove(uniqueId);

    const parentWidgetId = this.getWidget(uniqueId)?.parentId;

    const removedWidgetIds = this.guidelineGraphService.removeWidget(uniqueId);
    this._checkParentIfChanged(parentWidgetId);

    if (this.commentsService) {
      removedWidgetIds.forEach((id) => {
        this.commentsService.deleteCommentsByAnchorWidgetId(id);
      });
    }
  }

  private _syncParentOnAdd(widgetId: string, parentId: string): void {
    if (!this.publishedGuideline()) {
      return;
    }

    const parentWidget = this.getWidget<ChapterWidget>(parentId);
    if (!parentWidget) {
      return;
    }

    if (parentWidget.tag?.status === GuidelineTagStatus.NEW) {
      return;
    }

    const publishedParent = this.publishedGuideline()?.widgetMap?.[parentId] as ChapterWidget | undefined;
    const publishedWidgetIds = publishedParent?.widgetIds ?? [];

    const currentParent = this.getWidget<ChapterWidget>(parentId);
    const currentWidgetIds = currentParent?.widgetIds ?? [];

    const isDifferent =
      currentWidgetIds.length !== publishedWidgetIds.length || currentWidgetIds.some((id, i) => id !== publishedWidgetIds[i]);

    if (!isDifferent) {
      return;
    }

    const updatedParent = produce(currentParent, (draft) => {
      const updatedIds = new Set(draft.updatedWidgetIds ?? []);
      updatedIds.add(widgetId);
      draft.updatedWidgetIds = Array.from(updatedIds);

      draft.tag ??= {
        isActive: false,
        status: GuidelineTagStatus.CHILD_UPDATED,
      };
    });

    this.guidelineGraphService.updateElement(updatedParent.uniqueId, updatedParent);
  }

  private _syncParentOnRemove(uniqueId: string): void {
    if (!this.publishedGuideline()) {
      return;
    }

    const widget = this.getWidget(uniqueId);
    if (!widget || !widget.parentId) {
      return;
    }

    if (widget.widgetType === 'chapter' || widget.widgetType === 'component') {
      return;
    }

    const parentWidget = this.getWidget<ChapterWidget>(widget.parentId);
    if (!parentWidget) {
      return;
    }

    const existsInPublished = !!this.publishedGuideline()?.widgetMap?.[uniqueId];

    const updatedParent = produce(parentWidget, (draft) => {
      const currentMap = this.map();
      // Filter out stale IDs that no longer exist in the map (except the one being removed)
      const validIds = new Set(
        (draft.updatedWidgetIds ?? []).filter((id) => id !== uniqueId && (currentMap[id] || !!this.publishedGuideline()?.widgetMap?.[id])),
      );

      if (existsInPublished) {
        validIds.add(uniqueId);
        draft.tag ??= {
          isActive: false,
          status: GuidelineTagStatus.CHILD_UPDATED,
        };
      } else {
        if (validIds.size === 0 && draft.tag?.status === GuidelineTagStatus.CHILD_UPDATED) {
          draft.tag = undefined;
        }
      }

      draft.updatedWidgetIds = Array.from(validIds);

      // Remove the widget from the parent's widgetIds and childrenIds lists
      if (draft.widgetIds) {
        draft.widgetIds = draft.widgetIds.filter((id) => id !== uniqueId);
      }

      if (draft.childrenIds) {
        draft.childrenIds = draft.childrenIds.filter((id) => id !== uniqueId);
      }
    });

    this.guidelineGraphService.updateElement(updatedParent.uniqueId, updatedParent);
  }

  public updateGuidelineHeaderLocalChanges(data: Partial<GuidelineHeader>): void {
    this._headerLocalChanges.set({
      ...this._headerLocalChanges(),
      ...data,
    });

    const originalResourceTags = this._header()?.resourceTags ?? [];
    const currentResourceTags = this._headerLocalChanges()?.resourceTags ?? originalResourceTags;
    const hasChanged = !compareArrays(originalResourceTags, currentResourceTags, { ignoreOrder: true });
    this.updateChangeTracking('headerLocalChanges', hasChanged);
  }

  public updateGuidelineHeader(data: Partial<GuidelineHeader>): void {
    const originalHeader = this._header();

    this._header.set({
      ...originalHeader,
      ...data,
    });

    this.updateChangeTracking('header', compareGuidelineHeaders(this.originalGuideline(), this._header()));
  }

  public forceMarkWidgetChanged(widgetId: string): void {
    this.updateChangeTracking(widgetId, true);
  }

  private updateChangeTracking(uniqueId: string, hasChanged: boolean): void {
    this.updatedWidgetIds.update((ids) => {
      const exists = ids.includes(uniqueId);

      if (hasChanged && !exists) {
        return [...ids, uniqueId];
      }

      if (!hasChanged && exists) {
        return ids.filter((id) => id !== uniqueId);
      }

      return ids;
    });
  }

  public updateGuidelineMeta(data: Partial<EditableGuidelineMeta>): void {
    this._meta.set({
      ...this._meta(),
      ...data,
    });

    this.updateChangeTracking('header', compareGuidelineMeta(this.originalGuideline(), this._meta()));
  }

  public validateGuideline(): GuidelineValidationResult | null {
    const guidelineToValidate = this.guideline;

    if (!guidelineToValidate) {
      console.warn('No guideline to validate.');
    }

    return this.guidelineValidator.validate(guidelineToValidate ?? {});
  }

  public removeLinksByTargets(targetIds: string[]): void {
    this.guidelineGraphService.removeLinksByTargets(targetIds);
  }

  public getAllChildrenWidgets(uniqueId: string): GuidelineWidget[] {
    return GuidelineServiceUtil.getAllChildrenWidgets(uniqueId, this.map());
  }

  /**
   * Adds a widget to the guideline together with pre-built related widgets (e.g. component-list children).
   * Related widgets are inserted into the map directly without additional parent-wiring;
   * their parentId and widgetIds/childrenIds are already correctly set by the caller.
   */
  public addWidgetWithRelated(widget: GuidelineWidget, relatedWidgets: GuidelineWidget[], parentId: string, index?: number): void {
    const calculateTag = (widget: GuidelineWidget): GuidelineTag | undefined => {
      if (this.publishedGuideline() && !(widget.widgetType === 'text' && (widget as TextWidget).text !== '')) {
        return { isActive: true, status: GuidelineTagStatus.NEW };
      }
      return undefined;
    };

    for (const related of relatedWidgets) {
      related.tag = calculateTag(related);
      this.guidelineGraphService.addWidgetToMap(related);
    }

    widget.tag = calculateTag(widget);
    this.guidelineGraphService.addWidget(widget, parentId, index);

    this._syncParentOnAdd(widget.uniqueId, parentId);

    this._checkParentIfChanged(parentId);
  }

  public hasGuidelineChanged(): boolean {
    return this.hasUnsavedChanges();
  }
}
